// Inclusive 'YYYY-MM-DD' date range for the batch form. A record staged from
// this range spans start..end; end === start means a single day.

/** 'start' = the next calendar tap opens a new range, 'end' = it closes one. */
export type PickPhase = 'start' | 'end';

export interface DateRange {
  start: string; // 'YYYY-MM-DD', always set
  end: string; // 'YYYY-MM-DD', always >= start
  phase: PickPhase;
}

/** A collapsed range on a single day, ready for a fresh two-tap pick. */
export const singleDayRange = (dayKey: string): DateRange => ({ start: dayKey, end: dayKey, phase: 'start' });

/**
 * Two-tap calendar pick: the first tap collapses the range onto one day, the
 * second closes it — swapping the ends when the second day is the earlier one.
 */
export const applyRangePick = (range: DateRange, dayKey: string): DateRange => {
  if (range.phase === 'start') return { start: dayKey, end: dayKey, phase: 'end' };
  return dayKey < range.start
    ? { start: dayKey, end: range.start, phase: 'start' }
    : { start: range.start, end: dayKey, phase: 'start' };
};

/**
 * Set one end of the range directly from the From/To inputs. Moving an end past
 * the other drags that other end along rather than swapping them, so the field
 * the user just edited keeps showing exactly what they entered.
 */
export const setRangeEdge = (range: DateRange, which: 'start' | 'end', dayKey: string): DateRange => {
  if (!dayKey) return range;
  // A direct edit completes the range, so the next calendar tap opens a new one.
  return which === 'start'
    ? { start: dayKey, end: dayKey > range.end ? dayKey : range.end, phase: 'start' }
    : { start: dayKey < range.start ? dayKey : range.start, end: dayKey, phase: 'start' };
};
